import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Tag, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PREMIUM_IMAGE = "https://static.prod-images.emergentagent.com/jobs/3f8ee34a-b405-49e5-a6e6-3ec98fdd1fc1/images/9f57b325001eceafaba2954e9cc03880c00aa8e8e00171b13495fbc0f7ff3285.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/0n1q2nvp_My%20Direct%20debit%20app%20logo.jpg";

export default function UpgradePage() {
  const [plan, setPlan] = useState('yearly');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const prices = {
    monthly: { original: 5.0, label: '£5/month' },
    yearly: { original: 35.0, label: '£35/year', save: 'Save £25' }
  };

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`);
      setSubscription(data);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setValidating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/promo/validate`,
        { code: promoCode.trim(), plan },
        
      );
      setPromoApplied(data);
      toast.success(`Promo code applied! £${data.discount} off`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid promo code';
      toast.error(message);
      setPromoApplied(null);
    } finally {
      setValidating(false);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/checkout/create`,
        {
          plan,
          origin_url: window.location.origin,
          promo_code: promoApplied?.code || null
        },
        
      );
      
      // Redirect to Stripe
      window.location.href = data.url;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setLoading(false);
    }
  };

  const getFinalPrice = () => {
    if (promoApplied && promoApplied.valid) {
      return promoApplied.final_price;
    }
    return prices[plan].original;
  };

  const isPremium = subscription?.tier === 'premium';

  if (isPremium) {
    return (
      <PageLayout showNav isPremium={isPremium}>
        <div className="px-4 py-12 max-w-lg mx-auto text-center">
          <img src={LOGO_URL} alt="My Direct Debits" className="w-20 h-20 rounded-full mx-auto mb-6 object-contain" />
          <h1 className="font-heading font-bold text-dark text-3xl mb-4">
            You're Premium!
          </h1>
          <p className="text-text-secondary mb-8">
            You have full access to all features including unlimited direct debits.
          </p>
          <Link
            to="/dashboard"
            data-testid="back-to-dashboard-btn"
            className="btn-primary inline-block px-8 py-3"
          >
            Back to Dashboard
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showNav={false}>
      <div className="min-h-screen bg-page">
        {/* Header with premium image */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={PREMIUM_IMAGE} 
            alt="Premium" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-page"></div>
        </div>

        <div className="px-4 py-6 max-w-lg mx-auto -mt-16 relative">
          {/* Title Card */}
          <div className="card-default p-6 text-center mb-6 animate-fade-in">
            <img src={LOGO_URL} alt="My Direct Debits" className="w-16 h-16 rounded-full mx-auto mb-4 object-contain" />
            <h1 className="font-heading font-bold text-dark text-2xl mb-2">
              Unlock Premium
            </h1>
            <p className="text-text-secondary">
              Get unlimited direct debits and priority features
            </p>
          </div>

          {/* Features Comparison */}
          <div className="card-default p-6 mb-6 animate-fade-in">
            <h2 className="font-heading font-semibold text-dark text-lg mb-4">
              What you get
            </h2>
            <div className="space-y-3">
              {[
                { text: 'Up to 3 direct debits', free: true, premium: true },
                { text: 'Unlimited direct debits', free: false, premium: true },
                { text: 'Priority reminders', free: false, premium: true },
                { text: 'Export your data', free: false, premium: true },
              ].map((feature, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <span className="text-text-secondary">{feature.text}</span>
                  <div className="flex gap-6">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      feature.free ? 'bg-primary-light' : 'bg-gray-100'
                    }`}>
                      {feature.free && <Check className="w-4 h-4 text-primary" />}
                    </div>
                    <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-end gap-6 pt-2 text-xs text-text-light">
                <span className="w-6 text-center">Free</span>
                <span className="w-6 text-center text-gold font-semibold">Pro</span>
              </div>
            </div>
          </div>

          {/* Plan Selection */}
          <div className="space-y-3 mb-6 animate-fade-in">
            <button
              onClick={() => {
                setPlan('yearly');
                setPromoApplied(null);
              }}
              data-testid="plan-yearly"
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                plan === 'yearly'
                  ? 'border-gold bg-gold-light'
                  : 'border-border bg-white hover:border-gold/50'
              }`}
            >
              <div className="text-left">
                <span className="font-semibold text-dark">Yearly</span>
                {prices.yearly.save && (
                  <span className="ml-2 text-xs bg-gold text-white px-2 py-0.5 rounded-full">
                    {prices.yearly.save}
                  </span>
                )}
                <p className="text-sm text-text-secondary mt-1">Best value</p>
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                {prices.yearly.label}
              </span>
            </button>

            <button
              onClick={() => {
                setPlan('monthly');
                setPromoApplied(null);
              }}
              data-testid="plan-monthly"
              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                plan === 'monthly'
                  ? 'border-gold bg-gold-light'
                  : 'border-border bg-white hover:border-gold/50'
              }`}
            >
              <div className="text-left">
                <span className="font-semibold text-dark">Monthly</span>
                <p className="text-sm text-text-secondary mt-1">Flexible option</p>
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                {prices.monthly.label}
              </span>
            </button>
          </div>

          {/* Promo Code */}
          <div className="card-default p-4 mb-6 animate-fade-in">
            <p className="text-sm text-text-secondary mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Have a discount code?
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                data-testid="promo-code-input"
                className="input-field flex-1"
              />
              <Button
                onClick={handleValidatePromo}
                disabled={validating || !promoCode.trim()}
                data-testid="apply-promo-btn"
                className="btn-secondary px-4"
              >
                {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
            {promoApplied && (
              <div className="mt-3 p-3 bg-primary-light rounded-lg flex items-center gap-2">
                <Check className="w-5 h-5 text-primary" />
                <span className="text-sm text-primary-dark">
                  {promoApplied.code} applied: £{promoApplied.discount} off!
                </span>
              </div>
            )}
          </div>

          {/* Checkout Button */}
          <Button
            onClick={handleCheckout}
            disabled={loading}
            data-testid="checkout-btn"
            className="btn-premium w-full py-4 text-lg font-semibold mb-4"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              `Start Premium (${plan === 'yearly' ? 'Yearly' : 'Monthly'}) — £${getFinalPrice().toFixed(2)}`
            )}
          </Button>

          {/* Security Note */}
          <p className="text-center text-text-light text-xs flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            Secure payment powered by Stripe. Cancel anytime.
          </p>

          {/* Back Link */}
          <div className="text-center mt-6">
            <Link
              to="/dashboard"
              className="text-text-secondary hover:text-primary transition-colors"
            >
              Maybe later
            </Link>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
